// Turn any error (PostgREST, Edge Function, auth, network) into a message that
// is safe and helpful for users. Raw database errors are never shown.

import { phonesText } from '@core/business.ts';
import { getSite, supportEmail } from './site';

export class UserFacingError extends Error {
  constructor(
    message: string,
    public code: string = 'error',
    public details?: unknown,
  ) {
    super(message);
    this.name = 'UserFacingError';
  }
}

const HINT_MESSAGES: Record<string, string> = {
  no_access: "You don't have access to this question bank.",
  forbidden: "You don't have permission to do that.",
  auth_required: 'Please sign in to continue.',
  session_replaced: 'You were signed out because your account was signed in on another device.',
  get account_suspended() {
    return `Your account is suspended. Please contact ${supportEmail()} or call ${phonesText(getSite())}.`;
  },
  not_found: "We couldn't find that. It may have been removed.",
  already_owned: 'You already own this product — find it in My Question Banks.',
  own_product: "You can't purchase your own product.",
  already_subscribed: "You're already subscribed — find it in My Question Banks.",
  no_subscription: "This product isn't available by subscription.",
  subscription_only: 'This product is only available by monthly subscription.',
  invalid_discount_code: "That discount code isn't valid. Check it's typed correctly, and hasn't expired.",
  provider_unavailable: 'This product is temporarily unavailable for purchase.',
  exam_closed: 'This exam has already been submitted.',
  exam_expired: 'Time is up for this exam. Submit it to see your results.',
  no_questions: 'No questions match these settings.',
};

const AUTH_MESSAGES: Array<[RegExp, string]> = [
  [/invalid login credentials/i, 'Incorrect email or password.'],
  [/email not confirmed/i, 'Please confirm your email address first — check your inbox.'],
  [/user already registered/i, 'An account with this email already exists. Try signing in.'],
  [/password should be at least/i, 'Your password must be at least 8 characters.'],
  [/rate limit/i, 'Too many attempts. Please wait a moment and try again.'],
  [/failed to fetch|network/i, "We couldn't reach TradePrep. Check your connection and try again."],
  // Storage uploads
  [/exceeded the maximum allowed size|payload too large|entity too large/i, 'That file is too large.'],
  [/mime type .* is not supported|invalid mime/i, "That type of file isn't supported."],
  [/row-level security|row level security/i, "You don't have permission to upload there."],
];

interface PgLikeError {
  message?: string;
  hint?: string | null;
  code?: string;
  details?: string | null;
}

/** Validation-style RPC errors carry safe, human-written messages. */
const SAFE_MESSAGE_HINTS = new Set(['validation', 'invalid_state', 'locked', 'not_free', 'is_free', 'refund_exceeds', 'provider_suspended']);

export function toUserMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (!error) return fallback;
  if (error instanceof UserFacingError) return error.message;
  const e = error as PgLikeError;
  if (e.hint && HINT_MESSAGES[e.hint]) return HINT_MESSAGES[e.hint];
  if (e.hint && SAFE_MESSAGE_HINTS.has(e.hint) && e.message) return e.message;
  const msg = e.message ?? (typeof error === 'string' ? error : '');
  for (const [re, text] of AUTH_MESSAGES) if (re.test(msg)) return text;
  if (e.code === '42501') return "You don't have permission to do that.";
  if (e.code === 'PGRST116') return HINT_MESSAGES.not_found;
  return fallback;
}

export function errorCode(error: unknown): string | undefined {
  if (error instanceof UserFacingError) return error.code;
  return (error as PgLikeError)?.hint ?? (error as PgLikeError)?.code ?? undefined;
}
